import { useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Label } from '@antimatterhq/ui/components/label';
import { Button } from '@antimatterhq/ui/components/button';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import JoinTypeDropdown from '@/components/dropdown/JoinTypeDropdown/JoinTypeDropdown';
import { Section } from '@/components/container/section/Section';
import { FormInput } from '@/components/form/input/FormInput';
import { FormDatabricksPathAndTableValidatedInput } from '@/components/form/input/FormDatabricksPathAndTableValidatedInput';
import { FormError, FormSqlJoinInput } from '@/components/form/form';
import { FormValues } from '@/components/form/values/FormValues';
import {
  delayThresholdTooltip,
  dropDuplicatesTooltip,
  eventTimeColumnTooltip,
  joinTypeStatic,
  joinTypeStreamingBronze,
  tableAliasTooltip,
} from '@/strings/StringsTooltips';
import { StreamTableSchemaValues } from '@/zod/schemas/StreamTableSchema';

export type BaseStreamTableProps = {
  index: number;
  table: StreamTableSchemaValues;
  remove: (index: number) => void;
  prefix: string;
  hideRemove?: boolean;
  hideInputType?: boolean;
  tableSchema?: 'bronzeSchema' | 'silverSchema' | 'goldSchema';
};

type StreamTableProps =
  | (BaseStreamTableProps & {
      enableDatabricksTableValidation?: false;
      tableSchema?: never;
    })
  | (BaseStreamTableProps & {
      enableDatabricksTableValidation: true;
      tableSchema: 'bronzeSchema' | 'silverSchema' | 'goldSchema';
    });

export const StreamTable = ({
  index,
  table,
  remove,
  prefix,
  hideRemove,
  hideInputType,
  enableDatabricksTableValidation = false,
  tableSchema,
}: StreamTableProps) => {
  const { isSubmitting, handleChange, handleBlur, setFieldValue } =
    useFormikContext();
  return (
    <Section className="relative">
      <Label
        htmlFor={`${prefix}.${index}.name`}
        required
        id={`${prefix}.${index}.name-label`}
      >
        Table name
      </Label>

      {enableDatabricksTableValidation && tableSchema ? (
        <FormDatabricksPathAndTableValidatedInput
          name={`${prefix}.${index}.name`}
          id={`${prefix}.${index}.name`}
          disabled={isSubmitting}
          placeholder="Table name"
          constraint="mustExistRead"
          validationType="table"
          tableSchema={tableSchema}
          required
        />
      ) : (
        <FormInput
          name={`${prefix}.${index}.name`}
          value={table.name}
          onChange={handleChange}
          disabled={isSubmitting}
          placeholder="Table name"
          onBlur={handleBlur}
          aria-labelledby={`${prefix}.${index}.name-label`}
        />
      )}
      <FormError name={`${prefix}.${index}.name`} />

      <Spacer size="lg" />

      {!hideInputType && (
        <>
          <Label>Join type</Label>

          <ButtonGroup
            options={[
              {
                label: 'Static',
                value: 'static',
                info: { content: joinTypeStatic },
              },
              {
                label: 'Streaming',
                value: 'streaming',
                info: { content: joinTypeStreamingBronze },
              },
            ]}
            value={table.streaming ? 'streaming' : 'static'}
            onChange={(value) => {
              setFieldValue(
                `${prefix}.${index}.streaming`,
                value === 'streaming',
              );
            }}
            disabled={isSubmitting}
            id={`source-table-join-type-${index}`}
          />
        </>
      )}

      {table.streaming && (
        <>
          <Spacer size="lg" />
          <Label
            htmlFor={`${prefix}.${index}.watermark.eventTimeColumn`}
            info={eventTimeColumnTooltip}
            required
            id={`${prefix}.${index}.watermark.eventTimeColumn-label`}
          >
            Event time column
          </Label>

          <FormInput
            name={`${prefix}.${index}.watermark.eventTimeColumn`}
            value={table.watermark?.eventTimeColumn}
            onChange={handleChange}
            disabled={isSubmitting}
            aria-labelledby={`${prefix}.${index}.watermark.eventTimeColumn-label`}
          />
          <FormError name={`${prefix}.${index}.watermark.eventTimeColumn`} />

          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}.${index}.watermark.delayThreshold`}
            info={delayThresholdTooltip}
            required
            id={`${prefix}.${index}.watermark.delayThreshold-label`}
          >
            Delay threshold
          </Label>

          <FormInput
            name={`${prefix}.${index}.watermark.delayThreshold`}
            value={table.watermark?.delayThreshold}
            onChange={handleChange}
            disabled={isSubmitting}
            placeholder="Time duration, e.g. 1 hour or 15 minutes"
            aria-labelledby={`${prefix}.${index}.watermark.delayThreshold-label`}
          />
          <FormError name={`${prefix}.${index}.watermark.delayThreshold`} />

          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}.${index}.dropDuplicates`}
            info={dropDuplicatesTooltip}
            id={`${prefix}.${index}.dropDuplicates-label`}
          >
            Group by and drop duplicates
          </Label>

          <FormValues
            values={table.watermark?.dropDuplicates}
            onChange={handleChange}
            placeholder="Column name"
            name={`${prefix}.${index}.watermark.dropDuplicates`}
            aria-labelledby={`${prefix}.${index}.dropDuplicates-label`}
            data-testid={`${prefix}.${index}.watermark.dropDuplicates`}
          />
        </>
      )}

      <Spacer size="lg" />

      <Label
        htmlFor={`${prefix}.${index}.alias`}
        info={tableAliasTooltip}
        required={table.aliasRequired}
        id={`${prefix}.${index}.alias-label`}
      >
        Table alias
      </Label>

      <Spacer size="base" />

      <FormInput
        name={`${prefix}.${index}.alias`}
        value={table.alias}
        onChange={handleChange}
        disabled={isSubmitting}
        placeholder="Alias for the table"
        onBlur={handleBlur}
        aria-labelledby={`${prefix}.${index}.alias-label`}
      />

      <FormError name={`${prefix}.${index}.alias`} />

      {!table.joinHidden && (
        <>
          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}.${index}.joinType`}
            required
            id={`${prefix}.${index}.joinType-label`}
          >
            Join type
          </Label>

          <JoinTypeDropdown
            value={table.joinType}
            aria-labelledby={`${prefix}.${index}.joinType-label`}
            onValueChange={(value) =>
              handleChange({
                target: {
                  name: `${prefix}.${index}.joinType`,
                  value,
                },
              })
            }
            data-testid={`${prefix}.${index}.joinType-dropdown`}
          />
          <FormError name={`${prefix}.${index}.joinType`} />

          <Spacer size="lg" />

          <Label
            htmlFor={`${prefix}.${index}.joinExpr`}
            required
            id={`${prefix}.${index}.joinExpr-label`}
          >
            Join expression
          </Label>

          <FormSqlJoinInput
            name={`${prefix}.${index}.joinExpr`}
            value={table.joinExpr}
            onChange={handleChange}
            disabled={isSubmitting}
            onBlur={handleBlur}
            aria-labelledby={`${prefix}.${index}.joinExpr-label`}
          />
          <FormError name={`${prefix}.${index}.joinExpr`} />
        </>
      )}

      <Spacer size="lg" />

      {!hideRemove && (
        <div className="absolute right-2 top-2">
          <Button
            variant="ghost"
            size="small"
            iconOnly
            leftIcon="antimatterhq:trash"
            onClick={() => remove(index)}
            data-testid={`${prefix}.${index}.remove-button`}
          />
        </div>
      )}
    </Section>
  );
};
